import React, { useState, useEffect } from 'react';
import { letterAPI, userAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';
import {
  Button,
  Card,
  LoadingSpinner,
  Badge,
  ErrorMessage,
  SuccessMessage,
} from '../../UI';
import { 
  FileText, 
  Download, 
  Upload,
  Trash2,
  Eye,
  Search,
  Filter,
  Users,
  Calendar,
  ChevronDown,
  FileCheck,
  Loader2
} from 'lucide-react';

// Letter type options
const letterTypeOptions = [
  { value: 'offer', label: 'Offer Letter', color: 'blue' },
  { value: 'appointment', label: 'Appointment Letter', color: 'green' },
  { value: 'confirmation', label: 'Confirmation Letter', color: 'amber' },
  { value: 'experience', label: 'Experience Letter', color: 'red' },
  { value: 'relieving', label: 'Relieving Letter', color: 'purple' },
  { value: 'promotion', label: 'Promotion Letter', color: 'indigo' },
  { value: 'increment', label: 'Increment Letter', color: 'teal' },
  { value: 'warning', label: 'Warning Letter', color: 'orange' },
  { value: 'termination', label: 'Termination Letter', color: 'rose' },
  { value: 'other', label: 'Other', color: 'gray' },
];

const getLetterTypeColor = (type) => {
  const option = letterTypeOptions.find(o => o.value === type);
  const colorMap = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    red: 'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    teal: 'bg-teal-100 text-teal-700',
    orange: 'bg-orange-100 text-orange-700',
    rose: 'bg-rose-100 text-rose-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return colorMap[option?.color] || colorMap.gray;
};

const AdminLettersPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [allLetters, setAllLetters] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Active tab: 'uploaded' or 'generate'
  const [activeTab, setActiveTab] = useState('uploaded');
  
  // Auto-generate letter states
  const [generatingLetter, setGeneratingLetter] = useState({});
  const [generateSearchQuery, setGenerateSearchQuery] = useState('');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedType, setSelectedType] = useState('');
  
  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    employeeId: '',
    letterType: 'offer',
    title: '',
    description: '',
    issueDate: new Date().toISOString().split('T')[0],
    file: null
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch employees (for dropdowns) and all uploaded letters in parallel
      const [empResponse, lettersResponse] = await Promise.all([
        userAPI.getUsers({ limit: 1000 }),
        letterAPI.getAllUploadedLetters(),
      ]);

      const empList = empResponse.data?.users || empResponse.data || [];
      setEmployees(empList.filter(e => e.role !== 'admin'));

      // getAllUploadedLetters returns letters with employee populated as an object
      const rawLetters = lettersResponse.data?.data || lettersResponse.data || [];
      const normalized = rawLetters.map(letter => {
        const emp = letter.employee || {};
        return {
          ...letter,
          employee: emp._id || letter.employee,   // keep as ID string for filtering
          employeeName: emp.name || '',
          employeeUsername: emp.username || '',
          employeeDepartment: emp.department || '',
        };
      });
      setAllLetters(normalized);
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadLetter = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      setError('Please select a PDF file to upload');
      return;
    }
    if (!uploadForm.employeeId) {
      setError('Please select an employee');
      return;
    }
    if (!uploadForm.title) {
      setError('Please enter a title');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('letter', uploadForm.file);
      formData.append('letterType', uploadForm.letterType);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('issueDate', uploadForm.issueDate);

      await letterAPI.uploadLetter(uploadForm.employeeId, formData);
      setSuccess('Letter uploaded successfully');
      setShowUploadModal(false);
      setUploadForm({
        employeeId: '',
        letterType: 'offer',
        title: '',
        description: '',
        issueDate: new Date().toISOString().split('T')[0],
        file: null
      });
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to upload letter');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteLetter = async (letterId) => {
    if (!window.confirm('Are you sure you want to delete this letter?')) return;
    
    try {
      await letterAPI.deleteUploadedLetter(letterId);
      setSuccess('Letter deleted successfully');
      setAllLetters(prev => prev.filter(l => l._id !== letterId));
    } catch (err) {
      setError(err.message || 'Failed to delete letter');
    }
  };

  // Auto-generate letter handlers
  const handleGenerateLetter = async (employeeId, letterType) => {
    const key = `${employeeId}-${letterType}`;
    setGeneratingLetter(prev => ({ ...prev, [key]: true }));
    try {
      switch (letterType) {
        case 'offer':
          await letterAPI.getOfferLetter(employeeId);
          break;
        case 'appointment':
          await letterAPI.getAppointmentLetter(employeeId);
          break;
        case 'confirmation':
          await letterAPI.getConfirmationLetter(employeeId);
          break;
        case 'experience':
          await letterAPI.getExperienceLetter(employeeId);
          break;
        default:
          throw new Error('Unknown letter type');
      }
      setSuccess(`${letterType.charAt(0).toUpperCase() + letterType.slice(1)} letter generated successfully`);
    } catch (err) {
      setError(err.message || `Failed to generate ${letterType} letter`);
    } finally {
      setGeneratingLetter(prev => ({ ...prev, [key]: false }));
    }
  };

  // Filter employees for generate tab
  const filteredEmployeesForGenerate = employees.filter(emp => {
    if (!generateSearchQuery) return true;
    const query = generateSearchQuery.toLowerCase();
    return (
      emp.name?.toLowerCase().includes(query) ||
      emp.username?.toLowerCase().includes(query) ||
      emp.department?.toLowerCase().includes(query) ||
      emp.employeeId?.toLowerCase().includes(query)
    );
  });

  // Filter letters
  const filteredLetters = allLetters.filter(letter => {
    const matchesSearch = !searchQuery || 
      letter.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      letter.employeeName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      letter.employeeUsername?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEmployee = !selectedEmployee || letter.employee === selectedEmployee;
    const matchesType = !selectedType || letter.letterType === selectedType;
    return matchesSearch && matchesEmployee && matchesType;
  });

  // Group letters by employee
  const lettersByEmployee = filteredLetters.reduce((acc, letter) => {
    const empId = letter.employee;
    if (!acc[empId]) {
      acc[empId] = {
        employeeName: letter.employeeName,
        employeeUsername: letter.employeeUsername,
        employeeDepartment: letter.employeeDepartment,
        letters: []
      };
    }
    acc[empId].letters.push(letter);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && <ErrorMessage message={error} onClose={() => setError('')} />}
      {success && <SuccessMessage message={success} onClose={() => setSuccess('')} />}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Letters Management</h2>
          <p className="text-gray-500">Generate and manage employee letters</p>
        </div>
        {activeTab === 'uploaded' && (
          <Button 
            onClick={() => setShowUploadModal(true)}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Letter
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('uploaded')}
          className={`px-4 py-2 -mb-px font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'uploaded'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Upload className="w-4 h-4 inline mr-2" />
          Uploaded Letters
        </button>
        <button
          onClick={() => setActiveTab('generate')}
          className={`px-4 py-2 -mb-px font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'generate'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <FileCheck className="w-4 h-4 inline mr-2" />
          Generate Letters
        </button>
      </div>

      {activeTab === 'uploaded' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{allLetters.length}</p>
              <p className="text-sm text-gray-500">Total Letters</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(lettersByEmployee).length}</p>
              <p className="text-sm text-gray-500">Employees with Letters</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {allLetters.filter(l => l.letterType === 'offer' || l.letterType === 'appointment').length}
              </p>
              <p className="text-sm text-gray-500">Offer/Appointment</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {allLetters.filter(l => {
                  const date = new Date(l.createdAt);
                  const now = new Date();
                  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                }).length}
              </p>
              <p className="text-sm text-gray-500">This Month</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or employee name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          
          {/* Employee Filter */}
          <div className="w-full md:w-48">
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>{emp.name}</option>
              ))}
            </select>
          </div>
          
          {/* Type Filter */}
          <div className="w-full md:w-48">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All Types</option>
              {letterTypeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Letters List */}
      {filteredLetters.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Letters Found</h3>
          <p className="text-gray-500 mb-4">
            {allLetters.length === 0 
              ? 'Start by uploading letters for your employees'
              : 'No letters match your current filters'}
          </p>
          <Button onClick={() => setShowUploadModal(true)} className="bg-indigo-600 hover:bg-indigo-700">
            <Upload className="w-4 h-4 mr-2" />
            Upload First Letter
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(lettersByEmployee).map(([empId, data]) => (
            <Card key={empId} className="overflow-hidden">
              {/* Employee Header */}
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <span className="text-indigo-600 font-semibold">
                        {data.employeeName?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{data.employeeName}</h3>
                      <p className="text-sm text-gray-500">
                        @{data.employeeUsername} {data.employeeDepartment && `• ${data.employeeDepartment}`}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">{data.letters.length} letter{data.letters.length !== 1 ? 's' : ''}</Badge>
                </div>
              </div>
              
              {/* Letters */}
              <div className="divide-y divide-gray-100">
                {data.letters.map(letter => (
                  <div key={letter._id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{letter.title}</h4>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Badge className={`text-xs ${getLetterTypeColor(letter.letterType)}`}>
                            {letterTypeOptions.find(t => t.value === letter.letterType)?.label || letter.letterType}
                          </Badge>
                          <span>•</span>
                          <span>{new Date(letter.issueDate).toLocaleDateString('en-IN')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(letter.fileUrl, '_blank')}
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => letterAPI.downloadUploadedLetter(letter._id)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteLetter(letter._id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
        </>
      )}

      {/* Generate Letters Tab */}
      {activeTab === 'generate' && (
        <div className="space-y-4">
          {/* Info Card */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <FileCheck className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Auto-Generate Letters</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Generate official letters for any employee. Letters are created based on employee details 
                  and company information. Click on any letter type to download the PDF directly.
                </p>
              </div>
            </div>
          </Card>

          {/* Search */}
          <Card className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search employees by name, username, ID, or department..."
                value={generateSearchQuery}
                onChange={(e) => setGenerateSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </Card>

          {/* Employees List */}
          <div className="space-y-3">
            {filteredEmployeesForGenerate.length === 0 ? (
              <Card className="p-8 text-center">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500">No employees found</p>
              </Card>
            ) : (
              filteredEmployeesForGenerate.map(emp => (
                <Card key={emp._id} className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    {/* Employee Info */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-600 font-semibold text-lg">
                          {emp.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{emp.name}</h3>
                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                          <span>@{emp.username}</span>
                          {emp.employeeId && (
                            <>
                              <span>•</span>
                              <span>{emp.employeeId}</span>
                            </>
                          )}
                          {emp.department && (
                            <>
                              <span>•</span>
                              <span>{emp.department}</span>
                            </>
                          )}
                          {emp.designation && (
                            <>
                              <span>•</span>
                              <span>{emp.designation}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Generate Buttons */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGenerateLetter(emp._id, 'offer')}
                        disabled={generatingLetter[`${emp._id}-offer`]}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      >
                        {generatingLetter[`${emp._id}-offer`] ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Download className="w-4 h-4 mr-1" />
                        )}
                        Offer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGenerateLetter(emp._id, 'appointment')}
                        disabled={generatingLetter[`${emp._id}-appointment`]}
                        className="text-green-600 border-green-300 hover:bg-green-50"
                      >
                        {generatingLetter[`${emp._id}-appointment`] ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Download className="w-4 h-4 mr-1" />
                        )}
                        Appointment
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGenerateLetter(emp._id, 'confirmation')}
                        disabled={generatingLetter[`${emp._id}-confirmation`]}
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        {generatingLetter[`${emp._id}-confirmation`] ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Download className="w-4 h-4 mr-1" />
                        )}
                        Confirmation
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleGenerateLetter(emp._id, 'experience')}
                        disabled={generatingLetter[`${emp._id}-experience`]}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        {generatingLetter[`${emp._id}-experience`] ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Download className="w-4 h-4 mr-1" />
                        )}
                        Experience
                      </Button>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Letter</h3>
            <form onSubmit={handleUploadLetter} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee <span className="text-red-500">*</span>
                </label>
                <select
                  value={uploadForm.employeeId}
                  onChange={(e) => setUploadForm({ ...uploadForm, employeeId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                >
                  <option value="">Select Employee</option>
                  {employees.map(emp => (
                    <option key={emp._id} value={emp._id}>{emp.name} (@{emp.username})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Letter Type</label>
                <select
                  value={uploadForm.letterType}
                  onChange={(e) => setUploadForm({ ...uploadForm, letterType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {letterTypeOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                  placeholder="Enter letter title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  placeholder="Enter description"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Issue Date</label>
                <input
                  type="date"
                  value={uploadForm.issueDate}
                  onChange={(e) => setUploadForm({ ...uploadForm, issueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  PDF File <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
                {uploadForm.file && (
                  <p className="text-sm text-gray-500 mt-1">Selected: {uploadForm.file.name}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowUploadModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={uploading}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Upload Letter
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLettersPage;
